/**
 * Únete a FSG.
 * Dos rutas de entrada según el perfil del visitante (talento nuevo
 * vs. profesional con licencia) y los beneficios de pertenecer
 * al equipo.
 */

/* ---------------- Oportunidades por perfil ---------------- */
export const OPPORTUNITIES = [
  {
    id: "nuevo-talento",
    icon: "sparkles",
    eyebrow: { es: "Comienza desde cero", en: "Start from scratch" },
    title: { es: "Para nuevos talentos", en: "For new talent" },
    lead: {
      es: "¿Quieres ingresar a una de las industrias más rentables sin tener que buscar clientes por tu cuenta o salir a la calle? Te proveemos la tecnología, la prospección y la mentoría en vivo para que cierres ventas desde el primer día.",
      en: "Want to enter one of the most profitable industries without finding clients on your own or going door to door? We provide the technology, prospecting and live mentorship to help you close sales from day one.",
    },
    points: {
      es: [
        {
          title: "Sistema de leads incluido",
          text: "Te proporcionamos prospectos calificados para que tu único enfoque sea presentar y cerrar.",
        },
        {
          title: "Ventas 100% remotas",
          text: "Trabaja desde la comodidad de tu hogar u oficina con nuestro sistema telefónico y digital.",
        },
        {
          title: "Acompañamiento en citas virtuales",
          text: "Un mentor de alto rendimiento entra a tus llamadas y presentaciones iniciales para ayudarte a cerrar.",
        },
        {
          title: "Preparación y licencia",
          text: "Te guiamos paso a paso en tu proceso de licenciamiento ante la Oficina del Comisionado de Seguros.",
        },
      ],
      en: [
        {
          title: "Lead system included",
          text: "We provide qualified prospects so your only focus is presenting and closing.",
        },
        {
          title: "100% remote sales",
          text: "Work from the comfort of your home or office with our telephone and digital system.",
        },
        {
          title: "Support during virtual appointments",
          text: "A high-performing mentor joins your initial calls and presentations to help you close.",
        },
        {
          title: "Preparation and licensing",
          text: "We guide you step by step through the licensing process with the Office of the Insurance Commissioner.",
        },
      ],
    },
    cta: { es: "Quiero comenzar", en: "I want to get started" },
  },
  {
    id: "profesionales",
    icon: "trending",
    eyebrow: { es: "Lleva tu producción más lejos", en: "Take your production further" },
    title: {
      es: "Para representantes autorizados y líderes",
      en: "For licensed representatives and leaders",
    },
    lead: {
      es: "¿Cansado de la gestión presencial tradicional y de buscar prospectos por tu cuenta? Escala tu producción y construye un equipo de ventas remoto con una agencia que te respalda con tecnología y leads.",
      en: "Tired of traditional in-person management and finding prospects on your own? Scale your production and build a remote sales team with an agency that supports you with technology and leads.",
    },
    points: {
      es: [
        {
          title: "Estructura y liderazgo remoto",
          text: "Forma y gestiona tu propio equipo de asesores a distancia con atractivas sobrecomisiones (overrides).",
        },
        {
          title: "Flujo constante de leads",
          text: "Prospección centralizada para mantener activa la agenda de llamadas y cierres de todo tu equipo.",
        },
        {
          title: "Operación sin papeleo",
          text: "Nuestro equipo administrativo procesa las emisiones y solicitudes mientras tú y tu equipo se enfocan en vender.",
        },
        {
          title: "Respaldo multiaseguradora",
          text: "Cartera propia respaldada por más de ocho aseguradoras de primer nivel.",
        },
      ],
      en: [
        {
          title: "Remote structure and leadership",
          text: "Build and manage your own team of advisors remotely with attractive overrides.",
        },
        {
          title: "Consistent flow of leads",
          text: "Centralized prospecting keeps your entire team's call and closing calendar active.",
        },
        {
          title: "Paperless operation",
          text: "Our administrative team processes issues and applications while you and your team focus on selling.",
        },
        {
          title: "Multi-carrier support",
          text: "Your own book of business backed by more than eight leading insurance carriers.",
        },
      ],
    },
    cta: { es: "Escalar mi equipo remoto", en: "Scale my remote team" },
  },
];

/* ---------------- ¿Por qué FSG? ---------------- */
export const WHY_FSG = [
  {
    icon: "laptop",
    title: { es: "Ventas y operación remota", en: "Remote sales and operations" },
    text: {
      es: "Libertad y eficiencia total. Cierra ventas y atiende a tus clientes por teléfono y videollamada, sin perder tiempo en el tráfico ni asumir gastos innecesarios de movilidad.",
      en: "Total freedom and efficiency. Close sales and serve clients by phone and video call, without losing time in traffic or taking on unnecessary travel expenses.",
    },
  },
  {
    icon: "search",
    title: { es: "Prospección garantizada", en: "Guaranteed prospecting" },
    text: {
      es: "Leads listos para llamar. Olvídate de perseguir familiares o amigos: te proveemos herramientas y clientes potenciales calificados para que tu agenda siempre esté llena.",
      en: "Leads ready to call. Forget chasing relatives or friends: we provide tools and qualified prospects to keep your calendar full.",
    },
  },
  {
    icon: "trending",
    title: { es: "Cultura enfocada en seis cifras", en: "A six-figure culture" },
    text: {
      es: "Desarrollo de asesores top. Formamos profesionales preparados para alcanzar niveles de seis cifras anuales. Muchos de nuestros líderes actuales iniciaron desde cero en la plataforma.",
      en: "Developing top advisors. We train professionals to pursue six-figure annual performance. Many of our current leaders started from scratch on the platform.",
    },
  },
  {
    icon: "support",
    title: { es: "Apoyo administrativo dedicado", en: "Dedicated administrative support" },
    text: {
      es: "Tú vendes, nosotros tramitamos. Un equipo de operaciones se encarga del papeleo, seguimiento y emisión de tus pólizas para que no pierdas horas de llamadas productivas.",
      en: "You sell, we process. An operations team handles paperwork, follow-up and policy issuance so you do not lose productive calling hours.",
    },
  },
  {
    icon: "compass",
    title: { es: "Mentoría digital en vivo", en: "Live digital mentorship" },
    text: {
      es: "Cierres acompañados en remoto. Durante tus primeros 90 días, un mentor experimentado se conecta contigo en tus llamadas para fortalecer tu efectividad de cierre.",
      en: "Supported remote closings. During your first 90 days, an experienced mentor joins your calls to strengthen your closing effectiveness.",
    },
  },
  {
    icon: "plane",
    title: {
      es: "Convenciones y viajes de incentivo",
      en: "Conventions and international travel",
    },
    text: {
      es: "Premia tu rendimiento. Tu esfuerzo remoto te lleva a recorrer el mundo. Nuestro equipo ha calificado para convenciones e incentivos en Madrid, Argentina, Chile, Colombia y más.",
      en: "Reward your performance. Your remote effort can take you around the world. Our team has qualified for conventions and incentives in Madrid, Argentina, Chile, Colombia and beyond.",
    },
  },
  {
    icon: "money",
    title: {
      es: "Comisiones transparentes y sin techo",
      en: "Transparent, uncapped commissions",
    },
    text: {
      es: "Ingresos sin límite. Una estructura clara de comisiones, bonos por retención y calidad de cartera, diseñada para premiar tu volumen y liderazgo.",
      en: "Unlimited income potential. A clear structure of commissions and bonuses for retention and portfolio quality, designed to reward volume and leadership.",
    },
  },
];
